import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Megaphone, Users, Music, Trophy, Wifi, Compass } from "lucide-react";
import { SOLUTIONS, SERVICES, EXPLORE } from "../data/navigation";

const PILLAR_ICONS = { business: Briefcase, impact: Megaphone, people: Users, live: Music, play: Trophy, connect: Wifi };

const Home = () => (
  <main data-testid="home-page">
    {/* Hero */}
    <section className="relative min-h-[88vh] flex items-center bg-[#00142D] overflow-hidden">
      <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=2000&q=80" alt="Corporate conference audience" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 ev-hero-overlay" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 w-full">
        <div className="max-w-2xl">
          <p data-testid="hero-eyebrow" className="inline-flex items-center gap-2 text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#32B5E8] border border-[#32B5E8]/30 bg-[#32B5E8]/10 rounded-full px-4 py-2 mb-7">
            Innovate · Integrate · Elevate
          </p>
          <h1 data-testid="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-tight">
            Experiences that move <span className="ev-gradient-text">business forward.</span>
          </h1>
          <p data-testid="hero-subtext" className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">
            Evontra is a premium corporate event management and experiential events company — combining professional event execution with technology, creativity and innovation.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link data-testid="hero-plan-event-cta" to="/plan-your-event" className="ev-btn-primary inline-flex items-center gap-2 text-white text-sm font-bold px-7 py-3.5 rounded-full">
              Plan Your Event <ArrowRight size={16} />
            </Link>
            <Link data-testid="hero-explore-solutions-cta" to="/solutions" className="inline-flex items-center gap-2 text-sm font-bold px-7 py-3.5 rounded-full text-white border border-white/25 hover:bg-white/10 transition-colors">
              Explore Solutions
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Solution pillars */}
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#1679B5] mb-3">Solutions</p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#081D35] tracking-tight">Six worlds of Evontra</h2>
          <p className="mt-4 text-base text-[#64748B] leading-relaxed">
            From boardroom summits to stadium-scale productions, every Evontra solution is engineered for impact.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SOLUTIONS.map((s) => {
            const Icon = PILLAR_ICONS[s.id];
            return (
              <Link data-testid={`pillar-card-${s.id}`} key={s.id} to={`/solutions#${s.id}`} className="group relative rounded-2xl border border-slate-200 bg-[#F5F8FA] hover:bg-white p-7 transition-all duration-300 hover:shadow-[0_20px_45px_-20px_rgba(8,29,53,0.25)] hover:-translate-y-1 hover:border-[#32B5E8]/40">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1679B5] to-[#32B5E8] flex items-center justify-center mb-5">
                  <Icon size={20} className="text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#081D35]">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[#64748B]">{s.tagline}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#1679B5]">
                  Discover <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>

    {/* Services band (dark navy) */}
    <section className="py-24 bg-[#081D35] relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#1679B5]/20 blur-[120px]" />
      <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#6FAF2D]/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#32B5E8] mb-3">Services</p>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">End-to-end event capability</h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              Strategy, creative, production, technology and on-ground execution — one integrated team owning your event from first idea to final applause.
            </p>
            <Link data-testid="services-band-cta" to="/services" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#32B5E8] hover:text-white transition-colors">
              View All Services <ArrowRight size={15} />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {SERVICES.map((s) => (
              <span key={s} className="text-sm font-semibold text-slate-200 border border-white/15 bg-white/5 hover:bg-white/10 hover:border-[#32B5E8]/50 rounded-full px-4 py-2 transition-colors cursor-default">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Explore */}
    <section className="py-24 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
          <div className="max-w-xl">
            <p className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#6FAF2D] mb-3 flex items-center gap-2"><Compass size={14} /> Evontra Explore</p>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-[#081D35] tracking-tight">Discover the event ecosystem</h2>
          </div>
          <Link data-testid="explore-section-cta" to="/explore" className="inline-flex items-center gap-2 text-sm font-bold text-[#1679B5] hover:text-[#081D35] transition-colors">
            Open Explore <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {EXPLORE.map((g) => (
            <div key={g.title} className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-[0_18px_40px_-20px_rgba(8,29,53,0.2)] hover:-translate-y-1 transition-all duration-300">
              <p className="text-sm font-extrabold text-[#081D35] mb-4 pb-3 border-b border-slate-100">{g.title}</p>
              <ul className="space-y-2">
                {g.items.slice(0, 5).map((item) => (
                  <li key={item} className="text-sm text-[#64748B] flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#8BCB32]" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="relative py-28 bg-[#00142D] overflow-hidden">
      <img src="https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=2000&q=80" alt="Live event production" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#00142D] via-[#00142D]/85 to-[#1679B5]/40" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
        <h2 data-testid="cta-heading" className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight max-w-3xl mx-auto">
          Let's build your next <span className="ev-gradient-text">unforgettable event.</span>
        </h2>
        <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl mx-auto">
          Tell us about your goals — our team will craft a tailored event concept for you.
        </p>
        <Link data-testid="cta-plan-event-button" to="/plan-your-event" className="ev-btn-primary mt-9 inline-flex items-center gap-2 text-white text-sm font-bold px-8 py-4 rounded-full">
          Plan Your Event <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  </main>
);

export default Home;
